import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useCTAHandlers } from '~/ui-rocco/hooks/use-home-cta-actions';
import { CarouselPaginationPill } from '../components/CarouselPaginationPill';
import photoProjects from '../images/feature-strip/photo-projects.jpg';
import photoProfessionals from '../images/feature-strip/photo-professionals.jpg';
import photoProducts from '../images/feature-strip/photo-products.jpg';
import photoAccount from '../images/feature-strip/photo-account.jpg';
import photoShortlist from '../images/feature-strip/photo-shortlist.jpg';
import photoStartProject from '../images/feature-strip/photo-start-project.jpg';
import mediaCollaborate from '../images/feature-strip/media-collaborate.jpg';
import photoArticles from '../images/feature-strip/photo-articles.jpg';
import photoInbox from '../images/feature-strip/photo-inbox.jpg';
import mockSignupModal from '../images/feature-strip/mock-signup-modal.png';
import mockShortlist from '../images/feature-strip/mock-shortlist.png';
import mockInbox from '../images/feature-strip/mock-inbox.png';

/**
 * Feature strip (Figma `1422:27450` + pagination `997:38818`).
 *
 * A horizontally-scrolling carousel of nine navigation tiles in two themed
 * groups — "(Content – 01…04)" linking to the directories, and
 * "(Workspace – 01…05)" promoting member features behind the signup flow.
 * Each tile shows a description + pill CTA directly below its image.
 *
 * Workspace imagery composites exported app-UI mocks over photos, matching
 * the Figma treatment. Photos are design placeholders pending final art.
 */
interface FeatureTile {
    eyebrow: string;
    titleLines: [string, string];
    description: string;
    ctaLabel: string;
    /** Directory link; workspace tiles omit it and open the signup flow. */
    to?: string;
    image: string;
    /** Black overlay opacity over the image, matching the Figma treatment. */
    overlay: number;
    /** Dark blurred scrim between photo and mock (signup-modal tiles). */
    blurScrim?: boolean;
    /** Exported app-UI mock composited over the photo. */
    mock?: { src: string; alt: string; className: string };
}

const TILES: FeatureTile[] = [
    {
        eyebrow: '(Content – 01)',
        titleLines: ['Discover', 'Projects'],
        description:
            'Explore completed homes, find the designs that inspire you, and connect directly with the architects who can turn your vision into reality.',
        ctaLabel: 'Discover Projects',
        to: '/projects',
        image: photoProjects,
        overlay: 0.4,
    },
    {
        eyebrow: '(Content – 02)',
        titleLines: ['Connect with', 'Professionals'],
        description:
            "We've curated America's leading professionals so you can find the best fit for your project. Browse their work, discover their services and inquire directly from ArchiPro.",
        ctaLabel: 'Find a Pro',
        to: '/professionals',
        image: photoProfessionals,
        overlay: 0,
    },
    {
        eyebrow: '(Content – 03)',
        titleLines: ['Browse Products', '+ Materials'],
        description:
            'Explore products and materials architects love to use. From cladding and lighting to finishes and more. Our marketplace connects you with top brands ensuring access to the best materials to complete your space.',
        ctaLabel: 'Browse Products',
        to: '/products',
        image: photoProducts,
        overlay: 0.2,
    },
    {
        eyebrow: '(Content – 04)',
        titleLines: ['Editorial Insights', '+ Articles'],
        description:
            'Get informed and make the best decisions for your project. We bring you insights and interviews from those at the forefront of architecture and construction.',
        ctaLabel: 'Read Articles',
        to: '/articles',
        image: photoArticles,
        overlay: 0.2,
    },
    {
        eyebrow: '(Workspace – 01)',
        titleLines: ['Create', 'an Account'],
        description:
            'Set up your free account in seconds, save the homes and products you love, and unlock everything you need to manage your build in one place.',
        ctaLabel: 'Get Started',
        image: photoAccount,
        overlay: 0,
        blurScrim: true,
        mock: {
            src: mockSignupModal,
            alt: 'ArchiPro signup dialog',
            className: 'left-[6.4%] top-[41%] w-[55.2%]',
        },
    },
    {
        eyebrow: '(Workspace – 02)',
        titleLines: ['Start your', 'Project'],
        description:
            'Create a dedicated space for your build, capture every detail and decision along the way, and keep your project organised from first idea to final result.',
        ctaLabel: 'Get Started',
        image: photoStartProject,
        overlay: 0,
    },
    {
        eyebrow: '(Workspace – 03)',
        titleLines: ['Shortlist your', 'Ideas'],
        description:
            'Save the products, projects, and professionals that inspire you, organise them into boards, and build a clear picture of the home you want to create.',
        ctaLabel: 'Get Started',
        image: photoShortlist,
        overlay: 0,
        mock: {
            src: mockShortlist,
            alt: 'Save to project panel',
            className: 'left-[42.8%] top-[42.4%] w-[47.8%]',
        },
    },
    {
        eyebrow: '(Workspace – 04)',
        titleLines: ['Collaborate +', 'Invite Teams'],
        description:
            'Invite your architect, builder, and family into your project, share ideas and feedback in one place, and make decisions together as your vision takes shape.',
        ctaLabel: 'Get Started',
        // Flattened Figma composition (photo + invite modal + avatar row)
        image: mediaCollaborate,
        overlay: 0,
    },
    {
        eyebrow: '(Workspace – 05)',
        titleLines: ['Manage Inbox', 'and Enquiries'],
        description:
            'Keep every message and enquiry in a single inbox, track your conversations with professionals, and respond quickly to keep your project moving forward.',
        ctaLabel: 'Get Started',
        image: photoInbox,
        overlay: 0.2,
        mock: {
            src: mockInbox,
            alt: 'Project inbox conversation',
            className: 'left-[54.5%] top-[40.1%] w-[36.9%]',
        },
    },
];

const FeatureTileCard = ({ tile }: { tile: FeatureTile }) => {
    return (
        <>
            <div className="flex flex-col max-md:gap-[2.76vw] md:gap-[1.497vw]">
                <span className="font-sans leading-[1.25] tracking-[-0.01em] text-[#7f7f7f] opacity-70 max-md:text-[3.16vw] md:text-[1.711vw]">
                    {tile.eyebrow}
                </span>
                <span className="font-sans leading-none tracking-[-0.02em] text-[#1a1a1a] max-md:text-[5.92vw] md:text-[3.208vw]">
                    {tile.titleLines[0]}
                    <br />
                    {tile.titleLines[1]}
                </span>
            </div>
            <div className="relative aspect-square w-full overflow-hidden bg-black">
                <img
                    src={tile.image}
                    alt={`${tile.titleLines[0]} ${tile.titleLines[1]}`}
                    className="absolute inset-0 size-full max-w-none object-cover"
                    loading="lazy"
                />
                <div
                    className="absolute inset-0 bg-black"
                    style={{ opacity: tile.overlay }}
                />
                {tile.blurScrim && (
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
                )}
                {tile.mock && (
                    <img
                        src={tile.mock.src}
                        alt={tile.mock.alt}
                        className={cn(
                            'absolute h-auto max-w-none',
                            tile.mock.className,
                        )}
                        loading="lazy"
                    />
                )}
            </div>

            {/* Description + CTA below the image — always visible and readable
                on the white card (previously a hover overlay over the image). */}
            <div className="flex flex-col items-start max-md:gap-[3.1vw] md:gap-[1.563vw]">
                <p className="m-0 font-sans leading-[1.3] text-[#4c4c4c] max-md:text-[2.9vw] md:text-[1.042vw]">
                    {tile.description}
                </p>
                <span className="flex items-center justify-center rounded-full bg-[#1a1a1a] font-sans font-normal text-white max-md:h-[8.5vw] max-md:px-[5.6vw] max-md:text-[3.2vw] md:h-[2.565vw] md:px-[1.71vw] md:text-[1.14vw]">
                    {tile.ctaLabel}
                </span>
            </div>
        </>
    );
};

const FeatureStripPagination = () => {
    const { api, currentSlide, totalSlides, scrollPrev, scrollNext } =
        useCarousel();
    return (
        <div className="hidden md:flex md:justify-center md:py-[3.125vw]">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Explore ArchiPro"
            />
        </div>
    );
};

export const FeatureStripSection = () => {
    const { handleCTAClick, handleLoginClick } =
        useCTAHandlers('HomePageIntro');

    const onGetStarted = () => {
        handleCTAClick('startProject');
        handleLoginClick();
    };

    const cardClass =
        'group flex w-full flex-col text-left max-md:gap-[4.54vw] md:gap-[2.46vw]';

    return (
        <section className="w-full bg-white max-md:px-6 max-md:pb-[12vw] md:px-[1.927vw]">
            <Carousel
                autoPlay={{ delay: 4000 }}
                options={{
                    align: 'start',
                    dragFree: true,
                    duration: 35,
                    containScroll: 'trimSnaps',
                    // Wrap around at either end. Embla falls back to
                    // non-loop automatically when slides don't fill the view.
                    loop: true,
                }}
            >
                {/* Loop-safe spacing: symmetric per-slide padding (flex gap
                    breaks at the loop seam; centre align needs symmetry). */}
                <CarouselContent>
                    {TILES.map((tile) => (
                        <CarouselItem
                            key={tile.eyebrow}
                            className="max-md:basis-[calc(58%+2.27vw)] max-md:px-[1.135vw] md:basis-[31.823vw] md:px-[0.599vw]"
                        >
                            {tile.to ? (
                                <RoccoLink to={tile.to} className={cardClass}>
                                    <FeatureTileCard tile={tile} />
                                </RoccoLink>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onGetStarted}
                                    className={cardClass}
                                >
                                    <FeatureTileCard tile={tile} />
                                </button>
                            )}
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <FeatureStripPagination />
            </Carousel>
        </section>
    );
};
